import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Compass, ClipboardList, TrendingUp, Mail, Rocket, DraftingCompass, Cog, HardHat, Stamp, Scale, Calculator, Landmark, Ruler, Palette, TreePalm, Map } from "lucide-react";
import { Reveal, Counter, Kicker } from "@/components/motion";
import { UniverseCard, UniverseIcon } from "@/components/UniverseCard";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";

const METIER_ICONS = [Building2, Compass, ClipboardList, TrendingUp];
const QUICK_LINKS = ["/entreprendre#packs", "/accompagnement", "/investisseurs"];
// Univers par carte : vert entreprendre · rose mode de vie · bleu immobilier.
// `accent` = couleur d'univers (fond/pastille/halo) ; `text` = variante plus
// foncée réservée aux petits textes (kicker/CTA) pour un contraste accessible.
const QUICK_UNIVERSES = [
  { accent: "#2F9E44", text: "#1F7A33" },
  { accent: "#B3495E", text: "#9C3B50" },
  { accent: "#2E8BFF", text: "#1D6FD9" },
];
const QUICK_ICONS = [Rocket, Compass, TrendingUp];

/* Accès rapides — 3 portes d'entrée juste après le hero (Entreprendre / S'installer / Investir).
   Pattern « carte univers » (UniverseCard) : chaque carte incarne sa couleur. */
export const QuickAccess = () => {
  const { t } = useTranslation();
  const items = t("quickAccess.items", { returnObjects: true });
  return (
    <section className="border-b border-[#111827]/5 bg-white py-12 md:py-14" data-testid="quick-access-section">
      <div className="mx-auto grid max-w-7xl gap-4 px-5 sm:grid-cols-3 lg:px-8">
        {items.map((item, i) => {
          const Icon = QUICK_ICONS[i];
          const u = QUICK_UNIVERSES[i];
          return (
            <Reveal key={i} delay={i * 0.08} className="h-full">
              <UniverseCard to={QUICK_LINKS[i]} accent={u.accent} testid={`quick-access-${i}`} className="flex items-start gap-4 p-6">
                <UniverseIcon accent={u.accent} Icon={Icon} />
                <span className="relative">
                  <span className="block text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: u.text }}>{item.kicker}</span>
                  <span className="mt-1 block font-display text-lg leading-snug text-[#111827]">{item.title}</span>
                  <span className="mt-2 inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-semibold" style={{ color: u.text }}>
                    {item.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    {item.hint && (
                      <span className="ml-1 text-xs font-medium text-[#111827]/55" data-testid={`quick-access-hint-${i}`}>· {item.hint}</span>
                    )}
                  </span>
                </span>
              </UniverseCard>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export const Metier = () => {
  const { t } = useTranslation();
  const items = t("metier.items", { returnObjects: true });
  return (
    <section className="bg-[#F8FAFC] py-24 md:py-32" data-testid="metier-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Kicker>{t("metier.kicker")}</Kicker>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-[#111827] md:text-5xl">{t("metier.title")}</h2>
        </Reveal>
        <motion.div
          variants={bandGrid}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-12 grid gap-4 sm:grid-cols-2 md:mt-16 md:gap-5"
        >
          {items.map((item, i) => {
            const Icon = METIER_ICONS[i];
            return (
              <motion.div key={i} variants={bandItem} className="h-full">
                <div
                  data-testid={`metier-card-${i}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-[#111827]/8 bg-white p-6 transition-colors duration-300 hover:border-[#2E8BFF]/50 md:p-10"
                >
                  <span className="absolute right-6 top-6 font-display text-5xl text-[#111827]/5 transition-colors duration-500 group-hover:text-[#2E8BFF]/10 md:right-8 md:top-8 md:text-6xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#2E8BFF]/10 text-[#2E8BFF] transition-colors duration-300 group-hover:bg-[#2E8BFF] group-hover:text-white md:h-12 md:w-12 md:rounded-xl">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-xl text-[#111827] md:mt-7 md:text-2xl">{item.title}</h3>
                  <p className="mt-2.5 max-w-md text-sm font-light leading-relaxed text-slate-600 md:mt-3 md:text-base">{item.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Facilitateur de réseau — porte d'entrée pro (les accès grand public sont dans QuickAccess après le hero) */}
        <Reveal delay={0.15}>
          <div className="mt-6 grid gap-6 rounded-2xl border border-[#111827]/8 bg-white p-8 md:p-10 lg:grid-cols-[300px_1fr] lg:items-center" data-testid="home-gateways">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#2E8BFF]">{t("gateways.kicker")}</p>
              <p className="mt-3 font-display text-xl leading-snug text-[#111827]">{t("gateways.signature")}</p>
            </div>
            <Link
              to="/conseil-projets-immobiliers"
              data-testid="home-gateway-pro"
              className="group/g flex flex-col justify-between gap-4 rounded-xl border border-[#111827]/8 p-6 transition-colors duration-300 hover:border-[#2E8BFF]/50 hover:bg-[#2E8BFF]/[0.03] sm:flex-row sm:items-center"
            >
              <div>
                <p className="text-sm font-semibold text-[#111827]">{t("gateways.pro.title")}</p>
                <p className="mt-1.5 text-xs font-light leading-relaxed text-slate-500">{t("gateways.pro.desc")}</p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#2E8BFF]">
                {t("gateways.pro.cta")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/g:translate-x-1" />
              </span>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Steps = () => {
  const { t } = useTranslation();
  const items = t("steps.items", { returnObjects: true });
  return (
    <section className="bg-white py-24 md:py-32" data-testid="steps-section">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <Reveal className="text-left">
          <Kicker>{t("steps.kicker")}</Kicker>
          <h2 className="mt-4 font-display text-3xl tracking-tight text-[#111827] md:text-5xl">{t("steps.title")}</h2>
        </Reveal>
        <div className="relative mt-20">
          <div className="absolute bottom-4 left-[27px] top-4 w-px bg-gradient-to-b from-[#2E8BFF]/60 via-[#4DB8FF]/25 to-transparent md:left-[35px]" />
          <div className="space-y-14">
            {items.map((step, i) => (
              <Reveal key={i} delay={i * 0.06} data-testid={`step-item-${i + 1}`}>
                <div className="relative flex gap-7 md:gap-10">
                  <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#111827]/10 bg-white font-display text-lg text-[#2E8BFF] shadow-sm md:h-[72px] md:w-[72px] md:text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="pt-1.5 md:pt-4">
                    <h3 className="font-display text-xl text-[#111827] md:text-2xl">{step.title}</h3>
                    <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-slate-600 md:text-base">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Mêmes icônes et même ordre que la roue écosystème (cohérence + deep-link par index)
const ROLE_ICONS = [DraftingCompass, Cog, HardHat, Stamp, Scale, Calculator, Landmark, Ruler, Palette, TreePalm, Map];
const bandGrid = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};
const bandItem = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export const PartnerBand = () => {
  const { t } = useTranslation();
  const roles = t("partner.roles", { returnObjects: true });
  return (
    <section className="border-y border-[#111827]/5 bg-white py-14 md:py-16" data-testid="partner-band">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Kicker>{t("partner.band")}</Kicker>
          <p className="mt-3 hidden max-w-xl text-sm font-light leading-relaxed text-slate-600 sm:block">{t("partner.generic")}</p>
          <p className="mt-2 text-xs font-medium text-[#2E8BFF]">{t("partner.hint")}</p>
        </Reveal>
        <motion.div
          variants={bandGrid}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4"
        >
          {roles.map((r, i) => {
            const Icon = ROLE_ICONS[i];
            return (
              <motion.div key={r} variants={bandItem}>
                <Link
                  to={`/private-network?profession=${i}`}
                  aria-label={`${r} — ${t("partner.hint")}`}
                  className="group flex min-h-[56px] items-center gap-3 rounded-xl border border-[#111827]/8 bg-[#F8FAFC] p-3 transition-colors duration-300 hover:border-[#2E8BFF]/50 hover:bg-white md:p-3.5"
                  data-testid={`partner-role-${i}`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2E8BFF]/10 text-[#2E8BFF] transition-colors duration-300 group-hover:bg-[#2E8BFF] group-hover:text-white">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="text-xs font-semibold uppercase leading-tight tracking-wider text-[#111827]/75 transition-colors duration-300 group-hover:text-[#111827]">
                    {r}
                  </span>
                </Link>
              </motion.div>
            );
          })}
          <motion.div variants={bandItem}>
            <Link
              to="/private-network"
              className="group flex min-h-[56px] items-center justify-between gap-3 rounded-xl bg-[#111827] p-3 transition-colors duration-300 hover:bg-[#1D6FD9] md:p-3.5"
              data-testid="partner-discover-card"
            >
              <span className="text-xs font-semibold uppercase leading-tight tracking-wider text-white">{t("partner.discover")}</span>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white">
                <ArrowRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-0.5" />
              </span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export const Stats = () => {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState(undefined); // undefined = loading, null = API failed
  useEffect(() => {
    api.get("/settings/home-stats").then((r) => setSettings(r.data)).catch(() => setSettings(null));
  }, []);
  if (settings === undefined) return null;
  if (settings && settings.enabled === false) return null;
  const isEn = (i18n.language || "fr").startsWith("en");
  const items = settings
    ? (settings.items || [])
        .filter((s) => s.enabled !== false)
        .map((s) => ({ value: s.value, suffix: s.suffix, label: isEn ? s.label_en || s.label_fr : s.label_fr }))
    : t("stats.items", { returnObjects: true }); // fallback to i18n copy if the API is unreachable
  if (items.length === 0) return null;
  const colCls = items.length >= 4 ? "lg:grid-cols-4" : items.length === 3 ? "lg:grid-cols-3" : "";
  return (
    <section className="grain relative bg-[#111827] py-24 md:py-28" data-testid="stats-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Kicker light>{t("stats.kicker")}</Kicker>
          <h2 className="mt-4 font-display text-3xl text-white md:text-4xl">{t("stats.title")}</h2>
        </Reveal>
        <div className={`mt-14 grid grid-cols-2 gap-10 ${colCls}`}>
          {items.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`stat-item-${i}`}>
                <Counter to={s.value} suffix={s.suffix} className="font-display text-[clamp(1.75rem,3.4vw,3.25rem)] leading-tight text-white" />
                <div className="mt-3 h-px w-10 bg-[#4DB8FF]" />
                <p className="mt-3 text-sm font-light text-white/60">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ClosingCTA = () => {
  const { t } = useTranslation();
  return (
    <section id="terrain" className="grain relative overflow-hidden bg-[#111827] py-28 md:py-40" data-testid="closing-cta-section">
      <motion.div
        aria-hidden="true"
        className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-[#2E8BFF]/15 blur-3xl"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ repeat: Infinity, duration: 9, ease: "easeInOut" }}
      />
      <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
        <Reveal>
          <Kicker light className="text-center">{t("closing.kicker")}</Kicker>
          <h2 className="mt-5 font-display text-3xl leading-tight text-white md:text-5xl">{t("closing.title")}</h2>
          <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-white/70">{t("closing.desc")}</p>
          <div className="mt-10 flex flex-col items-center gap-5">
            <Link
              to="/j-ai-un-terrain"
              data-testid="closing-cta-btn"
              className="inline-flex items-center gap-2 rounded-xl shadow-sm bg-[#2E8BFF] px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105 hover:brightness-110"
            >
              {t("closing.cta")}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="text-xs text-white/45">{t("closing.note")}</p>
            <Link
              to="/accompagnement"
              data-testid="closing-alt-cta"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#4DB8FF] transition-colors duration-300 hover:text-white"
            >
              {t("closing.altCta")}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="closing-email-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#4DB8FF] transition-colors duration-300 hover:text-white"
            >
              <Mail className="h-4 w-4" />
              {SITE.email}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
